import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle, Copy01 } from "@untitledui/icons";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod/v3";
import { AuthClientError } from "@/api/auth-client-error";
import { useConfirmTwoFactorSetup } from "@/api/security";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useClipboard } from "@/hooks/use-clipboard";
import { globalHandleError } from "@/providers/QueryClientProviderWrapper";
import { authenticatorCodeSchema, groupSecret, INVALID_TOTP_CODE, secretFromTotpUri } from "@/utils/two-factor";
import { Eyebrow } from "./panel-heading";

const SetupKeyDialog = ({
  secret,
  open,
  onOpenChange,
}: {
  secret: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { copied, copy } = useClipboard();
  const grouped = groupSecret(secret);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-4 rounded-xl bg-white sm:max-w-[440px]">
        <DialogTitle className="text-lg leading-7 font-semibold text-gray-900">Your two-factor secret</DialogTitle>
        <DialogDescription className="text-sm text-gray-600">
          Enter manually in your authenticator app.
        </DialogDescription>
        <div className="flex items-center gap-2">
          <span
            data-testid="two-factor-secret"
            className="min-w-0 flex-1 rounded-lg bg-unbox-cream-50 px-3 py-2.5 text-center font-mono text-sm tracking-wide text-gray-800"
          >
            {grouped}
          </span>
          <button
            type="button"
            onClick={() => copy(secret)}
            aria-label={copied ? "Copied" : "Copy key"}
            title={copied ? "Copied" : "Copy key"}
            className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors duration-100 hover:bg-gray-50 hover:text-gray-900"
          >
            {copied ? (
              <CheckCircle className="size-[18px] text-success-600" aria-hidden />
            ) : (
              <Copy01 className="size-[18px]" aria-hidden />
            )}
          </button>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ScanPanel = ({
  totpURI,
  onCancel,
  onVerified,
}: {
  totpURI: string;
  onCancel: () => void;
  onVerified: () => void;
}) => {
  const [showSetupKey, setShowSetupKey] = useState(false);
  const confirmSetup = useConfirmTwoFactorSetup();
  const form = useForm<z.infer<typeof authenticatorCodeSchema>>({
    resolver: zodResolver(authenticatorCodeSchema),
    defaultValues: { code: "" },
  });

  const submit = async ({ code }: z.infer<typeof authenticatorCodeSchema>) => {
    try {
      await confirmSetup.mutateAsync(code);
      onVerified();
    } catch (err) {
      if (err instanceof AuthClientError && err.code === "INVALID_CODE") {
        form.setError("code", { message: INVALID_TOTP_CODE });
      } else {
        globalHandleError(err);
      }
    }
  };

  return (
    <>
      <form className="flex flex-col gap-6 p-6" onSubmit={form.handleSubmit(submit)} noValidate>
        <div className="flex flex-col gap-1">
          <Eyebrow>Step 2 of 3</Eyebrow>
          <h2 className="text-lg leading-7 font-semibold text-gray-900">Scan with your authenticator app</h2>
          <p className="max-w-[560px] text-sm text-gray-600">
            Open <span className="font-semibold">Google Authenticator</span> or any authenticator app in your phone,
            scan the QR code, then enter the 6-digit code it shows to prove the pairing worked.
          </p>
        </div>
        <div className="flex max-w-[520px] flex-col gap-6">
          <div className="w-fit rounded-xl bg-white p-4 shadow-xs">
            <QRCodeSVG value={totpURI} size={152} fgColor="#101828" title="QR code for your authenticator app" />
          </div>
          <Controller
            control={form.control}
            name="code"
            render={({ field, fieldState }) => (
              <Input
                label="Enter 6-digit code"
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={field.value}
                onChange={(value) => field.onChange(value.replace(/\D/g, "").slice(0, 6))}
                onBlur={field.onBlur}
                isInvalid={!!fieldState.error}
                hint={fieldState.error?.message}
                wrapperClassName="max-w-[240px]"
                inputClassName="px-4 py-3 font-mono text-xl tracking-[0.3em]"
              />
            )}
          />
          <button
            type="button"
            onClick={() => setShowSetupKey(true)}
            className="w-fit text-left text-[13px] font-medium text-gray-700 transition-colors duration-100 hover:text-gray-900"
          >
            <span className="text-brand-900 underline decoration-brand-300 decoration-dotted underline-offset-[3px]">
              Unable to scan?
            </span>{" "}
            You can use the setup key to manually configure it.
          </button>
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" isLoading={form.formState.isSubmitting}>
            Verify and continue
          </Button>
          <Button type="button" color="tertiary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
      <SetupKeyDialog secret={secretFromTotpUri(totpURI)} open={showSetupKey} onOpenChange={setShowSetupKey} />
    </>
  );
};
